import { Box } from "../Box";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";

import React, { type ReactNode, useMemo } from "react";
import { Platform, StyleSheet, View } from "react-native";

export interface LabelProps {
  /**
   * the string contents that should populate the label
   */
  text: string;

  /**
   * Unique React Native ID used to associate with the form field to its label for A11y
   */
  labelID?: string;

  /**
   * additional explainer text shown below the main text (optional).
   */
  explainerText?: string;

  /**
   * Unique React Native ID used to associate with the form field to its helper text for A11y
   */
  explainerTextID?: string;

  /**
   * styles the label as required by displaying an asterisk.
   */
  isRequired?: boolean;

  /**
   * A11y: Used for screen readers to announce the associated form field is required
   */
  requiredText?: string;

  /**
   * Text to display after the label to indicate the field is optional.
   * If provided and field is not required, displays the text after the label.
   */
  optionalText?: string;

  /**
   * Denotes the label has a form field that is invalid. Used for A11y
   */
  isInvalid?: boolean;

  /**
   * A11y: Used for screen readers to announce the associated form field is in an invalid state
   */
  invalidText?: string;

  /**
   * the icon displayed before the label (optional).
   */
  icon?: ReactNode;

  /**
   * Testing ID
   */
  testID?: string;

  /**
   * A11y: Render the screen reader text for "required" and "invalid" states
   * This is a workaround for a bug on Android with Placeholder Text where the label gets
   * overwritten by the placeholder text to the screen reader Talkback
   */
  renderStatesToScreenReader?: boolean;
}

/**
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=1525-3250&t=n5BogIowLkoI5i9b-1
 */
export function Label({
  text,
  labelID,
  explainerText,
  explainerTextID,
  isRequired = false,
  requiredText,
  optionalText,
  isInvalid = false,
  invalidText,
  icon,
  testID = "label-test-id",
  renderStatesToScreenReader = false,
}: LabelProps): React.JSX.Element {
  const theme = useTheme();
  const textColor = theme.colors.foregroundStrong;
  const styles = useMemo(
    () =>
      StyleSheet.create({
        box: {
          flexDirection: "row",
        },
        label: {
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 6,
        },
        // Wrapper for text content with nativeID - excludes icon from accessibilityLabelledBy
        labelTextWrapper: {
          position: "relative",
          flexShrink: 1,
        },
        container: {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        },
        // Android: Container for hidden accessibility text, absolutely positioned to remove from layout flow
        hiddenTextContainer: {
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
        },
        // Android: Text styles for hidden accessibility content read by TalkBack via accessibilityLabelledBy
        labelledByVisuallyHidden: {
          color: theme.colors.background,
          fontSize: 1,
          opacity: 0.0000001,
        },
      }),
    [theme],
  );

  const styledIcon = icon
    ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      React.cloneElement(icon as React.ReactElement<{ color?: string; "aria-hidden"?: boolean }>, {
        color: textColor,
        "aria-hidden": true,
      })
    : null;

  return (
    <View style={styles.container}>
      <View style={styles.label} testID={testID}>
        {styledIcon && <View>{styledIcon}</View>}
        {/* Wrapper with nativeID for accessibilityLabelledBy - contains only text content */}
        <View nativeID={labelID} style={styles.labelTextWrapper}>
          <Box sx={styles.box}>
            <Text level={3} sx={{ color: textColor, fontWeight: "bold" }}>
              {text}
              {isRequired && Platform.OS !== "web" && (
                <Text
                  ariaHidden
                  accessible={false}
                  sx={{
                    color: textColor,
                    fontWeight: "bold",
                  }}
                >
                  {"\u00A0*"}
                </Text>
              )}
              {optionalText && !isRequired && (
                <Text
                  sx={{
                    color: textColor,
                    fontWeight: "normal",
                  }}
                >
                  {"\u00A0"}
                  {optionalText}
                </Text>
              )}
            </Text>
            {/* Web: render asterisk in a View (div) wrapper so aria-hidden works.
                            Safari/VoiceOver ignores aria-hidden on <span> but respects it on <div>. */}
            {isRequired && Platform.OS === "web" && (
              <View aria-hidden={true} accessible={false}>
                <Text
                  level={3}
                  sx={{
                    color: textColor,
                    fontWeight: "bold",
                  }}
                >
                  {"\u00A0*"}
                </Text>
              </View>
            )}
          </Box>
          {/* Hidden text for Android TalkBack - rendered outside Text hierarchy with absolute positioning */}
          {renderStatesToScreenReader && (isRequired || isInvalid) && (
            <View style={styles.hiddenTextContainer}>
              <Text sx={styles.labelledByVisuallyHidden}>
                {isRequired && <>{requiredText ?? "required."}</>}{" "}
                {isInvalid && <>{invalidText ?? "invalid."}</>}
              </Text>
            </View>
          )}
        </View>
      </View>
      {explainerText && (
        <Text level={2} sx={{ color: theme.colors.foreground }} nativeID={explainerTextID}>
          {explainerText}
        </Text>
      )}
    </View>
  );
}
